import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Plus, Save, Trash2 } from "lucide-react";

import { useReplaceTemplate } from "@/features/availability/model/useAvailability";
import {
  rangesOverlap,
  templateByDay,
  toApiTime,
  toInputTime,
} from "@/features/availability/model/helpers";
import { DAY_LABELS_LONG } from "@/features/availability/types";
import type { TemplateWindow, TemplateWindowInput } from "@/features/availability/types";
import { cn } from "@/shared/lib/cn";
import { formatTimeRange12h } from "@/shared/lib/time";
import { Time12hPicker } from "@/shared/ui";

interface DraftWindow {
  start_time: string; // "HH:MM"
  end_time: string;
}

type DraftByDay = DraftWindow[][];

function toDraft(template: TemplateWindow[]): DraftByDay {
  return templateByDay(template).map((dayWindows) =>
    dayWindows.map((w) => ({
      start_time: toInputTime(w.start_time),
      end_time: toInputTime(w.end_time),
    })),
  );
}

function isEqualDraft(a: DraftByDay, b: DraftByDay): boolean {
  for (let d = 0; d < 7; d++) {
    if (a[d].length !== b[d].length) return false;
    for (let i = 0; i < a[d].length; i++) {
      if (a[d][i].start_time !== b[d][i].start_time) return false;
      if (a[d][i].end_time !== b[d][i].end_time) return false;
    }
  }
  return true;
}

function emptyDraft(): DraftByDay {
  return [[], [], [], [], [], [], []];
}

function isEmptyDraft(d: DraftByDay): boolean {
  return d.every((day) => day.length === 0);
}

function summarizeDay(windows: DraftWindow[]): string {
  if (windows.length === 0) return "Off";
  const sorted = [...windows].sort((a, b) => a.start_time.localeCompare(b.start_time));
  return sorted.map((w) => formatTimeRange12h(w.start_time, w.end_time)).join(", ");
}

interface Preset {
  id: string;
  label: string;
  description: string;
  apply: () => DraftByDay;
}

const PRESETS: Preset[] = [
  {
    id: "weekend-nights",
    label: "Weekend evenings",
    description: "Fri & Sat 5:00 PM – 11:00 PM",
    apply: () => {
      const d = emptyDraft();
      d[4] = [{ start_time: "17:00", end_time: "23:00" }]; // Fri
      d[5] = [{ start_time: "17:00", end_time: "23:00" }]; // Sat
      return d;
    },
  },
  {
    id: "weekday-evenings",
    label: "Weekday evenings",
    description: "Mon–Fri 5:00 PM – 11:00 PM",
    apply: () => {
      const d = emptyDraft();
      for (let i = 0; i <= 4; i++) d[i] = [{ start_time: "17:00", end_time: "23:00" }];
      return d;
    },
  },
  {
    id: "mornings",
    label: "Mornings",
    description: "Every day 6:00 AM – 12:00 PM",
    apply: () => {
      const d = emptyDraft();
      for (let i = 0; i < 7; i++) d[i] = [{ start_time: "06:00", end_time: "12:00" }];
      return d;
    },
  },
  {
    id: "full-time-days",
    label: "Full-time days",
    description: "Mon–Fri 9:00 AM – 5:00 PM",
    apply: () => {
      const d = emptyDraft();
      for (let i = 0; i <= 4; i++) d[i] = [{ start_time: "09:00", end_time: "17:00" }];
      return d;
    },
  },
];

interface WeeklyTemplateEditorProps {
  template: TemplateWindow[];
}

export function WeeklyTemplateEditor({ template }: WeeklyTemplateEditorProps) {
  const initial = useMemo(() => toDraft(template), [template]);
  const [draft, setDraft] = useState<DraftByDay>(initial);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [expandedDay, setExpandedDay] = useState<number | null>(null);
  const replace = useReplaceTemplate();

  // Reset draft whenever the upstream template changes (e.g. after save success).
  useEffect(() => {
    setDraft(toDraft(template));
    setValidationError(null);
  }, [template]);

  const isDirty = !isEqualDraft(draft, initial);
  const draftIsEmpty = isEmptyDraft(draft);

  const toggleDay = (day: number) => {
    setExpandedDay((prev) => (prev === day ? null : day));
  };

  const addWindow = (day: number) => {
    setDraft((prev) => {
      const next = prev.map((d) => [...d]);
      next[day] = [...next[day], { start_time: "09:00", end_time: "17:00" }];
      return next;
    });
  };

  const removeWindow = (day: number, index: number) => {
    setDraft((prev) => {
      const next = prev.map((d) => [...d]);
      next[day] = next[day].filter((_, i) => i !== index);
      return next;
    });
  };

  const updateWindow = (
    day: number,
    index: number,
    field: "start_time" | "end_time",
    value: string,
  ) => {
    setDraft((prev) => {
      const next = prev.map((d) => [...d]);
      next[day] = next[day].map((w, i) => (i === index ? { ...w, [field]: value } : w));
      return next;
    });
  };

  const applyPreset = (p: Preset) => {
    setDraft(p.apply());
    setValidationError(null);
  };

  const validate = (): string | null => {
    for (let d = 0; d < 7; d++) {
      const windows = draft[d];
      for (const w of windows) {
        if (!w.start_time || !w.end_time) return `${DAY_LABELS_LONG[d]}: pick a start and end time.`;
        if (w.end_time <= w.start_time)
          return `${DAY_LABELS_LONG[d]}: end time must be after start time.`;
      }
      for (let i = 0; i < windows.length; i++) {
        for (let j = i + 1; j < windows.length; j++) {
          if (
            rangesOverlap(
              windows[i].start_time,
              windows[i].end_time,
              windows[j].start_time,
              windows[j].end_time,
            )
          ) {
            return `${DAY_LABELS_LONG[d]}: overlapping time windows.`;
          }
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError(null);
    const flat: TemplateWindowInput[] = [];
    draft.forEach((dayWindows, dow) => {
      dayWindows.forEach((w) => {
        flat.push({
          day_of_week: dow,
          start_time: toApiTime(w.start_time),
          end_time: toApiTime(w.end_time),
        });
      });
    });
    await replace.mutateAsync({ windows: flat });
  };

  const handleReset = () => {
    setDraft(initial);
    setValidationError(null);
  };

  const handleClearAll = () => {
    setDraft(emptyDraft());
    setExpandedDay(null);
  };

  const totalWindows = draft.reduce((acc, d) => acc + d.length, 0);
  const daysWithWindows = draft.filter((d) => d.length > 0).length;

  // Day cells should call themselves "Off" only once at least one day has
  // windows; until then, the screen-wide banner is the source of truth and
  // saying "unavailable" on every empty day contradicts it.
  const showOffLabel = !draftIsEmpty;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Quick start
        </span>
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => applyPreset(p)}
            title={p.description}
            className="rounded-full border border-input bg-background px-3 py-1 text-xs hover:bg-surface-sunken"
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="divide-y rounded-md border bg-background">
        {DAY_LABELS_LONG.map((longLabel, dow) => {
          const hasWindows = draft[dow].length > 0;
          const isExpanded = expandedDay === dow;
          const summary = hasWindows
            ? summarizeDay(draft[dow])
            : showOffLabel
              ? "Off — unavailable this day"
              : "No hours set";
          return (
            <div key={longLabel}>
              <button
                type="button"
                onClick={() => toggleDay(dow)}
                aria-expanded={isExpanded}
                aria-controls={`day-panel-${dow}`}
                className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-surface-sunken sm:p-4"
              >
                <span className="w-20 shrink-0 text-sm font-medium sm:w-24">{longLabel}</span>
                <span
                  className={cn(
                    "min-w-0 flex-1 truncate text-sm",
                    hasWindows ? "text-foreground" : "text-muted-foreground",
                  )}
                  title={summary}
                >
                  {summary}
                </span>
                <ChevronDown
                  className={cn(
                    "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                    isExpanded && "rotate-180",
                  )}
                />
              </button>

              {isExpanded && (
                <div
                  id={`day-panel-${dow}`}
                  className="space-y-2 border-t bg-surface-sunken p-3 sm:p-4"
                >
                  {draft[dow].map((win, idx) => (
                    <div key={idx} className="flex flex-wrap items-center gap-2">
                      <Time12hPicker
                        value={win.start_time}
                        onChange={(v) => updateWindow(dow, idx, "start_time", v)}
                        ariaLabel={`${longLabel} window ${idx + 1} start`}
                      />
                      <span className="text-xs text-muted-foreground">–</span>
                      <Time12hPicker
                        value={win.end_time}
                        onChange={(v) => updateWindow(dow, idx, "end_time", v)}
                        ariaLabel={`${longLabel} window ${idx + 1} end`}
                      />
                      <button
                        type="button"
                        onClick={() => removeWindow(dow, idx)}
                        className="ml-auto rounded-md p-1.5 text-muted-foreground hover:bg-background hover:text-destructive"
                        aria-label="Remove window"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addWindow(dow)}
                    className="flex items-center gap-1 rounded-md border border-input bg-background px-2 py-1.5 text-xs hover:bg-background"
                    aria-label={`Add window to ${longLabel}`}
                  >
                    <Plus className="h-3.5 w-3.5" /> Add hours
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {validationError && (
        <div className="rounded-md border border-destructive bg-card p-3 text-sm text-destructive">
          {validationError}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {isDirty ? (
            <span className="rounded-full bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
              Unsaved changes
            </span>
          ) : !draftIsEmpty ? (
            <span className="flex items-center gap-1">
              <Check className="h-3 w-3" /> Saved · {totalWindows} window{totalWindows === 1 ? "" : "s"}{" "}
              across {daysWithWindows} day{daysWithWindows === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!draftIsEmpty && (
            <button
              type="button"
              onClick={handleClearAll}
              className="rounded-md px-2 py-1.5 text-xs text-muted-foreground hover:bg-surface-sunken hover:text-destructive"
            >
              Clear all
            </button>
          )}
          <button
            type="button"
            onClick={handleReset}
            disabled={!isDirty}
            className="rounded-md border border-input px-3 py-1.5 text-xs hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-40"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || replace.isPending}
            className={cn(
              "flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors",
              isDirty
                ? "bg-primary text-primary-foreground hover:opacity-90"
                : "border border-input text-muted-foreground",
              replace.isPending && "opacity-50",
            )}
          >
            <Save className="h-3.5 w-3.5" />
            {replace.isPending ? "Saving…" : "Save weekly schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}
